// UX-1 — Administration → Project Operations Control (admin-only panel).
// Aggregation layer over the EXISTING flag/activation endpoints: global
// switch, module switches, per-project activation, eligibility explanations,
// disable-impact safety and the append-only audit trail. Backend enforces
// project_ops_admin.view / .edit — this page never relies on hiding alone.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useFormTab } from "@/hooks/useFormTab";
import ProjectWorkspace from "@/components/ProjectWorkspace";
import { OpsSwitchConfirm } from "@/components/OpsSwitchConfirm";
import {
  SlidersHorizontal, ShieldAlert, RefreshCw, Search, X, Eye,
  ExternalLink, History, Users as UsersIcon, CheckCircle2, XCircle,
} from "lucide-react";

const FLAG_ENDPOINT = {
  project_ops: "/project-ops/settings",
  project_grn: "/project-ops/grn-settings",
  project_pi: "/project-ops/pi-settings",
  project_material_consumption: "/project-ops/pmc-settings",
  work_progress: "/project-ops/wp-settings",
  project_po_approval: "/project-ops/po-approval-settings",
  project_po_conversion: "/project-ops/po-conversion-settings",
  project_direct_ops_po: "/project-ops/direct-po-settings",
};

function LastChange({ c }) {
  if (!c) return <span className="text-[10px] text-slate-400">never changed</span>;
  return (
    <span className="text-[10px] text-slate-500">
      {formatDate(c.at)} · {c.actor}{c.reason ? <span className="italic"> — “{c.reason}”</span> : null}
    </span>
  );
}

function StatePill({ on, testid }) {
  return (
    <span data-testid={testid}
      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
        on ? "bg-emerald-50 text-emerald-800 border-emerald-300"
           : "bg-slate-100 text-slate-600 border-slate-300"}`}>
      {on ? "ON" : "OFF"}
    </span>
  );
}

function EligibilityModal({ project, onClose }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/project-ops/projects/${project.id}/eligibility`)
      .then(r => setData(r.data)).catch(e => toast.error(e?.response?.data?.detail || "Load failed"));
  }, [project.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-10 overflow-y-auto" data-testid="ops-eligibility-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 p-5 space-y-3 text-xs">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <UsersIcon size={15} /> Eligibility — {project.project_code} · {project.project_name}
          </div>
          <button data-testid="ops-eligibility-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        {!data ? <div className="text-slate-500">Loading…</div> : (
          <>
            {data.explanations.length > 0 && (
              <div className="border border-amber-200 bg-amber-50/70 rounded p-2" data-testid="ops-effective-explanations">
                <div className="font-bold text-amber-900 mb-1">Why modules may be unavailable right now</div>
                <ul className="list-disc ml-4 text-[11px] text-amber-900">
                  {data.explanations.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            <div className="text-[11px] text-slate-600">Assignment mode: <b>{data.assignment_mode}</b></div>
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="w-full text-[11px]" data-testid="ops-eligibility-table">
                <thead className="bg-slate-100 text-[9px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-2 py-1.5 text-left">User</th>
                    <th className="px-2 py-1.5 text-left">Branch</th>
                    <th className="px-2 py-1.5 text-left">Assigned</th>
                    <th className="px-2 py-1.5 text-left">Ops view permissions held</th>
                    <th className="px-2 py-1.5 text-left">Missing</th>
                    <th className="px-2 py-1.5 text-left">Effective</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map(u => (
                    <tr key={u.id} className="border-t border-slate-100 align-top" data-testid={`ops-elig-${u.email}`}>
                      <td className="px-2 py-1"><b>{u.name}</b><div className="text-slate-500">{u.email}{u.role === "admin" ? " · admin" : ""}</div></td>
                      <td className="px-2 py-1">{u.branch_ok ? "✓ in scope" : "✗ outside scope"}</td>
                      <td className="px-2 py-1">{u.assigned ? "✓" : "✗"}</td>
                      <td className="px-2 py-1 max-w-[200px]">{Object.entries(u.modules).filter(([, v]) => v).map(([k]) => k).join(", ") || "—"}</td>
                      <td className="px-2 py-1 max-w-[200px] text-rose-700">{u.missing.join("; ") || "—"}</td>
                      <td className="px-2 py-1">
                        {u.eligible
                          ? <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><CheckCircle2 size={11} /> Eligible</span>
                          : <span className="inline-flex items-center gap-1 text-rose-700 font-bold"><XCircle size={11} /> Not Eligible</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2">
              <button data-testid="ops-elig-users-link" onClick={() => navigate("/users")}
                className="px-2 py-1 font-bold border border-slate-300 rounded hover:bg-slate-50 inline-flex items-center gap-1">
                <ExternalLink size={11} /> Users &amp; Roles
              </button>
              <button data-testid="ops-elig-projects-link" onClick={() => navigate("/projects")}
                className="px-2 py-1 font-bold border border-slate-300 rounded hover:bg-slate-50 inline-flex items-center gap-1">
                <ExternalLink size={11} /> Project Edit
              </button>
              <div className="text-[10px] text-slate-400 self-center">Read-only view — permissions are managed only in the existing screens.</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ProjectOpsControlPage() {
  const { user, hasPerm } = useAuth();
  const openFormTab = useFormTab();
  const [panel, setPanel] = useState(null);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [confirm, setConfirm] = useState(null); // {title, turningOff, impactParams, apply}
  const [elig, setElig] = useState(null);
  const [audit, setAudit] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const allowed = user?.role === "admin" || hasPerm("project_ops_admin", "view");

  const loadPanel = useCallback(() => {
    api.get("/project-ops/control-panel").then(r => setPanel(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Load failed"));
  }, []);
  const loadRows = useCallback(() => {
    api.get("/project-ops/projects-activation", { params: { q, page, limit: 10 } })
      .then(r => { setRows(r.data.rows); setTotal(r.data.total); })
      .catch(() => setRows([]));
  }, [q, page]);
  useEffect(() => { if (allowed) loadPanel(); }, [allowed, loadPanel]);
  useEffect(() => { if (allowed) loadRows(); }, [allowed, loadRows]);
  useEffect(() => {
    if (allowed && showAudit) api.get("/project-ops/audit", { params: { limit: 30 } }).then(r => setAudit(r.data)).catch(() => setAudit([]));
  }, [allowed, showAudit, panel, rows]);

  const canEdit = !!panel?.can_edit;

  const flagSwitch = (flagId, label, current) => setConfirm({
    title: `${label}: turn ${current ? "OFF" : "ON"}?`,
    turningOff: current,
    impactParams: { scope: flagId === "project_ops" ? "global" : "module", module_key: flagId },
    apply: async (reason) => {
      await api.put(FLAG_ENDPOINT[flagId], { enabled: !current, reason, request_id: crypto.randomUUID() });
      toast.success(`${label} switched ${!current ? "ON" : "OFF"}`);
      setConfirm(null); loadPanel(); loadRows();
    },
  });

  const projectSwitch = (p) => setConfirm({
    title: `${p.project_code} — turn Project Operations ${p.ops_state === "ON" ? "OFF" : "ON"}?`,
    turningOff: p.ops_state === "ON",
    impactParams: { scope: "project", project_id: p.id },
    apply: async (reason) => {
      await api.put(`/project-ops/projects/${p.id}`, { enabled: p.ops_state !== "ON", reason, request_id: crypto.randomUUID() });
      toast.success(`${p.project_code} activation updated`);
      setConfirm(null); loadPanel(); loadRows();
    },
  });

  const openWorkspace = (p) => openFormTab({
    path: `/projects/workspace/${p.id}`,
    label: `${p.project_code || "Project"} · Workspace`,
    parent: "/settings/project-ops-control",
    render: ({ close }) => <ProjectWorkspace project={p} opsGlobalOn={!!panel?.global?.enabled} onClose={close} />,
  });

  const pages = useMemo(() => Math.max(1, Math.ceil(total / 10)), [total]);

  if (!allowed) {
    return (
      <div className="border border-slate-200 bg-slate-50/60 rounded-lg py-16 px-6 flex flex-col items-center text-center" data-testid="ops-control-denied">
        <ShieldAlert size={28} className="text-slate-400 mb-3" />
        <div className="text-sm font-bold text-slate-700">Project Operations Control requires the project_ops_admin permission</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ops-control-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-base font-bold text-slate-900 flex items-center gap-2">
            <SlidersHorizontal size={17} /> Project Operations Control
          </div>
          <div className="text-[11px] text-slate-500 max-w-3xl">{panel?.read_only_note}</div>
        </div>
        <button data-testid="ops-control-refresh" onClick={() => { loadPanel(); loadRows(); }}
          className="px-2 py-1.5 text-xs font-bold border border-slate-300 rounded hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* 1 — Global switch */}
      <div className="border border-slate-300 rounded-lg bg-white p-4 flex items-center justify-between flex-wrap gap-2" data-testid="ops-global-card">
        <div>
          <div className="text-sm font-bold text-slate-900">Project Operations (Global)</div>
          <LastChange c={panel?.global?.last_change} />
          <div className="text-[10px] text-slate-400">{panel?.projects_activated ?? "—"} project(s) currently activated</div>
        </div>
        <div className="flex items-center gap-3">
          <StatePill on={!!panel?.global?.enabled} testid="ops-global-state" />
          {canEdit && panel && (
            <button data-testid="ops-global-toggle" onClick={() => flagSwitch("project_ops", "Project Operations (Global)", panel.global.enabled)}
              className="px-3 py-1.5 text-xs font-bold border border-slate-400 rounded hover:bg-slate-50">
              Turn {panel.global.enabled ? "OFF" : "ON"}…
            </button>
          )}
        </div>
      </div>

      {/* 2 — Module switches */}
      <div className="border border-slate-300 rounded-lg bg-white p-4 space-y-2" data-testid="ops-modules-card">
        <div className="text-sm font-bold text-slate-900">Module Switches</div>
        <table className="w-full text-xs">
          <thead className="text-[9px] uppercase tracking-wider text-slate-500 text-left">
            <tr><th className="py-1">Module</th><th className="py-1">Status</th><th className="py-1">Last changed</th><th className="py-1">Note</th><th className="py-1" /></tr>
          </thead>
          <tbody>
            {(panel?.modules || []).map(m => (
              <tr key={m.key} className="border-t border-slate-100" data-testid={`ops-module-${m.key}`}>
                <td className="py-1.5 font-bold">{m.label}</td>
                <td className="py-1.5">
                  {m.flag_id
                    ? <StatePill on={!!m.enabled} testid={`ops-module-state-${m.key}`} />
                    : <span className="text-[10px] text-slate-500 italic" data-testid={`ops-module-state-${m.key}`}>no separate flag — follows Global + Project</span>}
                </td>
                <td className="py-1.5"><LastChange c={m.last_change} /></td>
                <td className="py-1.5 text-[10px] text-slate-500 max-w-[320px]">{m.note}</td>
                <td className="py-1.5 text-right">
                  {m.flag_id && canEdit && (
                    <button data-testid={`ops-module-toggle-${m.key}`} onClick={() => flagSwitch(m.flag_id, m.label, !!m.enabled)}
                      className="px-2 py-1 text-[11px] font-bold border border-slate-300 rounded hover:bg-slate-50">
                      Turn {m.enabled ? "OFF" : "ON"}…
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 3 — Per-project activation */}
      <div className="border border-slate-300 rounded-lg bg-white p-4 space-y-2" data-testid="ops-projects-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm font-bold text-slate-900">Per-Project Activation</div>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2 text-slate-400" />
            <input data-testid="ops-projects-search" className="border border-slate-300 rounded pl-6 pr-2 py-1 text-xs w-64"
              placeholder="Search code / name / client…" value={q}
              onChange={e => { setQ(e.target.value); setPage(1); }} />
          </div>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded">
          <table className="w-full text-[11px]" data-testid="ops-projects-table">
            <thead className="bg-slate-100 text-[9px] uppercase tracking-wider text-slate-600 text-left">
              <tr>
                <th className="px-2 py-1.5">Code</th><th className="px-2 py-1.5">Name</th>
                <th className="px-2 py-1.5">Branch</th><th className="px-2 py-1.5">Client</th>
                <th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5">Ops</th>
                <th className="px-2 py-1.5">Users</th><th className="px-2 py-1.5">Effective modules</th>
                <th className="px-2 py-1.5">Last changed</th><th className="px-2 py-1.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows === null ? (
                <tr><td colSpan={10} className="px-3 py-4 text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-4 text-slate-400">No projects match</td></tr>
              ) : rows.map(p => (
                <tr key={p.id} className="border-t border-slate-100 align-top" data-testid={`ops-project-row-${p.project_code}`}>
                  <td className="px-2 py-1.5 font-mono font-bold">{p.project_code}</td>
                  <td className="px-2 py-1.5 max-w-[180px] truncate" title={p.project_name}>{p.project_name}</td>
                  <td className="px-2 py-1.5">{p.branch_name || "—"}</td>
                  <td className="px-2 py-1.5 max-w-[140px] truncate">{p.client_name || "—"}</td>
                  <td className="px-2 py-1.5">{p.status}</td>
                  <td className="px-2 py-1.5">
                    <span data-testid={`ops-project-state-${p.project_code}`}
                      className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${
                        p.ops_state === "ON" ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                          : p.ops_state === "OFF" ? "bg-amber-50 text-amber-800 border-amber-300"
                          : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                      {p.ops_state === "NEVER" ? "OFF (never)" : p.ops_state}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">{p.authorized_users_count > 0 ? `${p.authorized_users_count} assigned` : "unrestricted"}</td>
                  <td className="px-2 py-1.5 max-w-[220px] text-[10px] text-slate-600">
                    {p.effective_modules.length ? p.effective_modules.join(", ") : <span className="text-slate-400">none effective</span>}
                  </td>
                  <td className="px-2 py-1.5"><LastChange c={p.last_change} /></td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {canEdit && (
                      <button data-testid={`ops-project-toggle-${p.project_code}`} onClick={() => projectSwitch(p)}
                        className="text-slate-800 hover:underline font-bold mr-2">
                        {p.ops_state === "ON" ? "Disable…" : "Enable…"}
                      </button>
                    )}
                    <button data-testid={`ops-project-elig-${p.project_code}`} onClick={() => setElig(p)}
                      className="text-sky-700 hover:underline mr-2 inline-flex items-center gap-0.5"><Eye size={10} /> Eligibility</button>
                    <button data-testid={`ops-project-workspace-${p.project_code}`} onClick={() => openWorkspace(p)}
                      className="text-slate-600 hover:underline inline-flex items-center gap-0.5"><ExternalLink size={10} /> Workspace</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span>Page {page} of {pages} · {total} project(s)</span>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-0.5 border rounded disabled:opacity-40">Prev</button>
          <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="px-2 py-0.5 border rounded disabled:opacity-40">Next</button>
        </div>
      </div>

      {/* 4 — Audit history */}
      <div className="border border-slate-300 rounded-lg bg-white p-4 space-y-2" data-testid="ops-audit-card">
        <button data-testid="ops-audit-toggle" onClick={() => setShowAudit(s => !s)}
          className="text-sm font-bold text-slate-900 inline-flex items-center gap-2">
          <History size={14} /> Control Audit Trail (append-only) {showAudit ? "▾" : "▸"}
        </button>
        {showAudit && (
          <div className="max-h-72 overflow-y-auto text-[11px]">
            {(audit || []).map((a, i) => (
              <div key={a.id || i} className="border-t border-slate-100 py-1">
                <span className="font-mono text-slate-400">{formatDate(a.at)}</span> · <b>{a.action}</b> · {a.entity} <span className="font-mono">{String(a.entity_id).slice(0, 12)}</span> · {a.actor}
                {a.before !== undefined && <span className="text-slate-500"> · {JSON.stringify(a.before)} → {JSON.stringify(a.after)}</span>}
                {a.reason && <span className="italic text-slate-600"> — “{a.reason}”</span>}
              </div>
            ))}
            {audit && audit.length === 0 && <div className="text-slate-400">No audit entries</div>}
          </div>
        )}
      </div>

      {confirm && (
        <OpsSwitchConfirm title={confirm.title} turningOff={confirm.turningOff}
          impactParams={confirm.impactParams} onConfirm={confirm.apply}
          onClose={() => setConfirm(null)} />
      )}
      {elig && <EligibilityModal project={elig} onClose={() => setElig(null)} />}
    </div>
  );
}
